import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine, FadeUp } from "./Reveal";

const ParallaxImg = ({ src, alt }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden transition-transform duration-700 ease-out group-hover:scale-[1.04]">
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className="h-[116%] w-full object-cover will-change-transform"
        loading="lazy"
      />
    </div>
  );
};

const ASSETS = "https://customer-assets-agu9un31.emergentagent.net/job_northedge-pro/artifacts";

const PROJECTS = [
  {
    name: "Cast Planter Walls",
    cat: "Commercial Concrete",
    year: "2025",
    cls: "md:col-span-7 h-[380px] md:h-[540px]",
    img: `${ASSETS}/xnv005gu_IMG_8736.webp`,
  },
  {
    name: "Formwork & Rebar",
    cat: "Commercial Concrete",
    year: "2025",
    cls: "md:col-span-5 h-[380px] md:h-[540px]",
    img: `${ASSETS}/jn6osqcp_IMG_8733.webp`,
  },
  {
    name: "Front Porch Footing",
    cat: "Residential Concrete",
    year: "2025",
    cls: "md:col-span-5 h-[320px] md:h-[440px]",
    img: `${ASSETS}/w8z63nwm_IMG_8575.webp`,
  },
  {
    name: "Porch & Stair Formwork",
    cat: "Residential Concrete",
    year: "2025",
    cls: "md:col-span-7 h-[320px] md:h-[440px]",
    img: `${ASSETS}/e3ilx4qp_IMG_8579.webp`,
  },
  {
    name: "Foundation Wall Forms",
    cat: "Residential Concrete",
    year: "2025",
    cls: "md:col-span-6 h-[300px] md:h-[460px]",
    img: `${ASSETS}/3fxvbrfj_IMG_8577.webp`,
  },
  {
    name: "Whiteout Protocol",
    cat: "Commercial Snow Contract",
    year: "2026",
    cls: "md:col-span-6 h-[300px] md:h-[460px]",
    img: "https://images.pexels.com/photos/11680712/pexels-photo-11680712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const Projects = () => (
  <section id="work" className="py-24 sm:py-32 lg:py-48" data-testid="projects-section">
    <div className="max-w-[1800px] mx-auto px-6 sm:px-12 lg:px-24">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-16 lg:mb-24">
        <div>
          <FadeUp>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF3B00] mb-8">Selected work</p>
          </FadeUp>
          <h2 className="font-oswald font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-7xl leading-[0.95]" data-testid="projects-heading">
            <MaskedLine>Proof, poured</MaskedLine>
            <MaskedLine delay={0.12}>
              in <span className="text-outline">concrete.</span>
            </MaskedLine>
          </h2>
        </div>
        <FadeUp delay={0.2}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#808080]">
            2025 — 2026 Job Sites
          </p>
        </FadeUp>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {PROJECTS.map((p, i) => (
          <FadeUp key={p.name} delay={0.06 * i} className={p.cls}>
            <div
              className="group relative h-full w-full overflow-hidden border border-[#262626] spotlight"
              data-testid={`project-tile-${i + 1}`}
            >
              <ParallaxImg src={p.img} alt={p.name} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/95 via-[#050505]/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute top-5 right-5 font-mono text-xs tracking-[0.2em] text-[#808080] group-hover:text-[#FF3B00] transition-colors duration-500">
                {p.year}
              </span>
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B00] mb-2">{p.cat}</p>
                <h3 className="font-oswald font-bold uppercase tracking-tight text-2xl sm:text-4xl text-[#F2F2F2]">
                  {p.name}
                </h3>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);
